/**
 * A cutscene's picture as a pure function of (scene data, t) (docs/archive/OPENING.md section 4.4).
 *
 * The World runs the scene's clock (packages/shared/src/scene.ts); the client reads `world.scene`
 * and draws what `sceneFrame` says is on screen at that moment: where the camera looks and how
 * close, the fade, the ink panel, each actor (a puppet: never an entity), the effects, captions and
 * the line being spoken. Nothing here feeds back into the world.
 *
 * Pose, sfx, fx and panel names in data/scenes.ts are the client's: the ones listed here are drawn
 * or played; `SCENE_POSES` maps each pose to what stands in for it until it is drawn (phase B art).
 */
import { EMOJI_SECONDS, type Cue, type Focus, type Point, type SceneDef, type Spot } from '@nine/shared';

/** What a scene's frame needs from the world: spots as points, and where actors stand by default. */
export interface StageHost {
  /** A spot on the map as a point on open ground (`world.story.point`). */
  point(spot: Spot): Point;
  /** Where an actor stands before any cue places it (the entity it stands in for, or the player). */
  home(actor: string): Point;
}

/** How each scene pose is drawn for now: an emote (data/emotes), a townsfolk act, or standing. */
export const SCENE_POSES: Readonly<Record<string, { emote?: string; act?: string; gesture?: 'bow' }>> = {
  idle: {},
  // `lie` and `palm` (phase B) stand in as sitting and thinking.
  sit: { emote: 'lounge', act: 'sit' },
  lie: { emote: 'lounge', act: 'sit' },
  fish: { act: 'fish' },
  glance: {},
  think: { emote: 'think' },
  palm: { emote: 'think' },
  fold: { emote: 'fold' },
  point: { emote: 'point' },
  salute: { emote: 'salute' },
  bow: { emote: 'salute', gesture: 'bow' },
};

/** The scene sounds the client plays (audio.ts); `music` cues name a theme. */
export const SCENE_SFX: readonly string[] = [
  'gong',
  'waves',
  'hit',
  'hurt',
  'ching',
  'nine',
  'heartbeat',
  'whisper',
  'boom',
  'charge',
  'land',
  'palmGlow',
  'shrineHum',
  // The plains opening (data/plainsOpening.ts).
  'rain',
  'letter',
  'lights',
  'flash',
  'bird',
];
export const SCENE_MUSIC: readonly string[] = ['none', 'town', 'field', 'snow', 'battle', 'boss', 'jail'];
export const SCENE_FX: readonly string[] = ['palmGlow', 'shrineHum'];

export type FadeColor = 'black' | 'white' | 'ink';

export interface ActorFrame {
  x: number;
  y: number;
  /** Direction faced, in tiles, or null when no cue has turned it. */
  face: readonly [number, number] | null;
  moving: boolean;
  pose: string;
  /** Seconds since the pose began. */
  poseAge: number;
  emoji: { id: string; age: number } | null;
}

export interface SceneFrame {
  /** Where the camera looks (tiles), or null to follow the player as usual. */
  focus: Point | null;
  /** The scene's pixel scale (data units: 4 is the usual view), or null for the player's own. */
  px: number | null;
  fade: { color: FadeColor; a: number };
  /** The ink panel up (panel scenes), with the one before it while it dissolves in. */
  panel: { art: string; age: number; prev: string | null } | null;
  actors: Record<string, ActorFrame>;
  fx: { fx: string; actor: string | null; at: Point; age: number; dur: number }[];
  captions: { text: string; style: Extract<Cue, { do: 'caption' }>['style']; age: number; dur: number }[];
  line: { actor: string; name: string; text: string; age: number; dur: number } | null;
}

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
const ease = (k: number) => k * k * (3 - 2 * k);
const lerp = (a: number, b: number, k: number) => a + (b - a) * k;

/** Every actor's place, facing, pose and emoji at `t`. */
function actorsAt(def: SceneDef, t: number, host: StageHost): Record<string, ActorFrame> {
  const out: Record<string, ActorFrame> = {};
  for (const id of Object.keys(def.actors)) {
    const home = host.home(id);
    out[id] = { x: home.x, y: home.y, face: null, moving: false, pose: 'idle', poseAge: t, emoji: null };
  }
  for (const c of def.cues) {
    if (c.at > t) break;
    if (!('actor' in c) || !c.actor) continue;
    const a = out[c.actor];
    if (!a) continue;
    if (c.do === 'place') {
      const p = host.point(c.spot);
      a.x = p.x;
      a.y = p.y;
      if (c.face) a.face = c.face;
    } else if (c.do === 'walk') {
      const from = { x: a.x, y: a.y };
      const to = host.point(c.to);
      const k = c.dur > 0 ? clamp01((t - c.at) / c.dur) : 1;
      a.x = lerp(from.x, to.x, k);
      a.y = lerp(from.y, to.y, k);
      a.moving = k < 1;
      if (Math.hypot(to.x - from.x, to.y - from.y) > 0.05) a.face = [to.x - from.x, to.y - from.y];
      // Walking ends a held pose; the next pose cue sets one again.
      if (a.moving) a.pose = 'idle';
    } else if (c.do === 'pose') {
      a.pose = c.pose;
      a.poseAge = t - c.at;
    } else if (c.do === 'emoji') {
      a.emoji = t - c.at < EMOJI_SECONDS ? { id: c.emoji, age: t - c.at } : null;
    }
  }
  return out;
}

/** A camera focus as a point, at time `t`. */
function focusAt(def: SceneDef, f: Focus, t: number, host: StageHost): Point {
  if ('actor' in f) {
    const a = actorsAt(def, t, host)[f.actor];
    return a ? { x: a.x, y: a.y } : host.home('me');
  }
  if ('between' in f) {
    const all = actorsAt(def, t, host);
    const [p, q] = [all[f.between[0]], all[f.between[1]]];
    if (!p || !q) return host.home('me');
    return { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 };
  }
  return host.point(f);
}

/** The camera at `t`: its focus (panning between cues) and its pixel scale. */
function cameraAt(def: SceneDef, t: number, host: StageHost): { focus: Point | null; px: number | null } {
  let focus: Point | null = null;
  let px: number | null = null;
  const cams = def.cues.filter((c): c is Extract<Cue, { do: 'camera' }> => c.do === 'camera' && c.at <= t);
  for (let i = 0; i < cams.length; i++) {
    const c = cams[i]!;
    const last = i === cams.length - 1;
    // Where the camera was when this pan began: the last cue's focus then, else on the player.
    const from =
      i > 0 ? focusAt(def, cams[i - 1]!.focus, c.at, host) : focusAt(def, { actor: 'me' }, c.at, host);
    const fromPx: number | null = px;
    const k = c.dur && c.dur > 0 ? ease(clamp01(((last ? t : cams[i + 1]!.at) - c.at) / c.dur)) : 1;
    const to = focusAt(def, c.focus, last ? t : cams[i + 1]!.at, host);
    focus = { x: lerp(from.x, to.x, k), y: lerp(from.y, to.y, k) };
    if (c.px != null) px = fromPx == null ? c.px : Math.round(lerp(fromPx, c.px, k));
  }
  return { focus, px };
}

/** The fade over the screen at `t`. */
function fadeAt(def: SceneDef, t: number): { color: FadeColor; a: number } {
  let color: FadeColor = 'black';
  let ramp: { from: number; to: number; at: number; dur: number } | null = null;
  const value = (r: NonNullable<typeof ramp>, at: number) =>
    r.dur <= 0 ? (at >= r.at ? r.to : r.from) : lerp(r.from, r.to, clamp01((at - r.at) / r.dur));
  for (const c of def.cues) {
    if (c.at > t) break;
    if (c.do !== 'fade') continue;
    const from: number = ramp ? value(ramp, c.at) : 0;
    ramp = { from, to: c.to === 'clear' ? 0 : 1, at: c.at, dur: c.dur };
    if (c.to !== 'clear') color = c.to;
  }
  return { color, a: ramp ? value(ramp, t) : 0 };
}

/** Everything on screen at `t` (seconds) of a scene. */
export function sceneFrame(def: SceneDef, t: number, host: StageHost): SceneFrame {
  const actors = actorsAt(def, t, host);
  const { focus, px } = def.kind === 'stage' ? cameraAt(def, t, host) : { focus: null, px: null };
  let panel: SceneFrame['panel'] = null;
  const fx: SceneFrame['fx'] = [];
  const captions: SceneFrame['captions'] = [];
  let line: SceneFrame['line'] = null;
  let lastArt: string | null = null;
  for (const c of def.cues) {
    if (c.at > t) break;
    const age = t - c.at;
    if (c.do === 'panel') {
      panel = { art: c.art, age, prev: lastArt };
      lastArt = c.art;
    } else if (c.do === 'fx' && age < c.dur) {
      const a = c.actor ? actors[c.actor] : undefined;
      const at = a ? { x: a.x, y: a.y } : c.spot ? host.point(c.spot) : host.home('me');
      fx.push({ fx: c.fx, actor: c.actor ?? null, at, age, dur: c.dur });
    } else if (c.do === 'caption' && age < c.dur)
      captions.push({ text: c.text, style: c.style, age, dur: c.dur });
    else if (c.do === 'say' && age < c.dur)
      line = { actor: c.actor, name: c.name, text: c.text, age, dur: c.dur };
  }
  return { focus, px, fade: fadeAt(def, t), panel, actors, fx, captions, line };
}

/** The prompt of the wait the scene is stopped at, if it is waiting. */
export function waitPrompt(def: SceneDef, t: number): string | null {
  let best: Extract<Cue, { do: 'wait' }> | null = null;
  for (const c of def.cues) if (c.do === 'wait' && c.at <= t + 1e-6) best = c;
  return best?.prompt ?? null;
}

/** The sound and music cues passed going from `t0` (exclusive; −1 at the start) to `t1`. */
export function cuesCrossed(def: SceneDef, t0: number, t1: number): Extract<Cue, { do: 'sfx' | 'music' }>[] {
  return def.cues.filter(
    (c): c is Extract<Cue, { do: 'sfx' | 'music' }> =>
      (c.do === 'sfx' || c.do === 'music') && c.at > t0 && c.at <= t1,
  );
}

/** The music theme a scene has set by `t`, or null (the game's own then). */
export function musicAt(def: SceneDef, t: number): string | null {
  let theme: string | null = null;
  for (const c of def.cues) if (c.do === 'music' && c.at <= t) theme = c.theme;
  return theme;
}

/** How much of a line is shown `age` seconds into it: typed out over a little under its time. */
export function typed(text: string, age: number, dur: number): string {
  const chars = [...text];
  const secs = Math.min(dur * 0.6, chars.length / 28);
  const n = secs <= 0 ? chars.length : Math.ceil(chars.length * clamp01(age / secs));
  return chars.slice(0, n).join('');
}
